import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, Divider, CircularProgress, Link,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { filesAPI, projectsAPI } from '../../services/api';
import DriveFolderPicker from './DriveFolderPicker';

/**
 * Carpetas de Drive asociadas al proyecto (oferta y proyecto).
 *
 * Las carpetas historicas se crearon con nombres arbitrarios, asi que no hay
 * forma de encontrarlas por plantilla: aqui se asocian a mano.
 */
export default function ProjectFolderLinks({ project, onChange }) {
  const [roots, setRoots] = useState(null);
  const [picker, setPicker] = useState(null);   // 'offer' | 'project'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    filesAPI.roots()
      .then((r) => setRoots(r.data))
      .catch(() => setRoots(null));
  }, []);

  if (!roots || roots.backend !== 'drive') return null;

  const run = async (fn, successText) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await fn();
      if (onChange) onChange(resp.data);
      setNotice(successText);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido completar la operación');
    } finally {
      setBusy(false);
      setPicker(null);
    }
  };

  const handleSelect = (kind) => async (folderId) => {
    await run(
      () => projectsAPI.linkDriveFolder(project.id, folderId, kind),
      kind === 'offer' ? 'Carpeta de oferta asociada' : 'Carpeta de proyecto asociada',
    );
  };

  const rows = [
    {
      kind: 'offer',
      label: 'Carpeta de la oferta',
      folderId: project.drive_offer_folder_id,
      path: project.drive_offer_path,
      root: roots.offers || roots.explorer,
      onCreate: () => run(() => projectsAPI.createOfferFolders(project.id),
        'Carpeta de oferta creada'),
      hint: 'Rama OFERTAS · se crea sola al dar de alta la petición',
    },
    {
      kind: 'project',
      label: 'Carpeta del proyecto',
      folderId: project.drive_folder_id,
      path: project.documents_path,
      root: roots.projects || roots.explorer,
      onCreate: () => run(() => projectsAPI.createFolders(project.id),
        'Carpeta de proyecto creada'),
      hint: 'Rama PROYECTOS · pulsa "Crear" para copiar la carpeta de la oferta',
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Carpetas en Google Drive</Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {rows.map((row, i) => (
        <Box key={row.kind}>
          {i > 0 && <Divider sx={{ my: 1.5 }} />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <FolderIcon sx={{ color: row.folderId ? '#f5a623' : 'text.disabled' }} />
            <Box sx={{ minWidth: 200, flexGrow: 1 }}>
              <Typography variant="body2" fontWeight="medium">{row.label}</Typography>
              {row.folderId ? (
                <Link
                  href={`https://drive.google.com/drive/folders/${row.folderId}`}
                  target="_blank" rel="noopener noreferrer"
                  variant="caption" underline="hover"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  {row.path || 'Ver en Drive'}
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              ) : (
                <Typography variant="caption" color="text.secondary">{row.hint}</Typography>
              )}
            </Box>

            {row.folderId ? (
              <>
                <Chip label="Asociada" size="small" color="success" variant="outlined" />
                <Button size="small" startIcon={<LinkIcon />} disabled={busy}
                  onClick={() => setPicker(row.kind)}>
                  Cambiar
                </Button>
                <Button size="small" color="error" startIcon={<LinkOffIcon />} disabled={busy}
                  onClick={() => run(
                    () => projectsAPI.unlinkDriveFolder(project.id, row.kind),
                    'Asociación eliminada (no se ha borrado nada en Drive)',
                  )}>
                  Quitar
                </Button>
              </>
            ) : (
              <>
                <Button size="small" variant="outlined" startIcon={<LinkIcon />} disabled={busy}
                  onClick={() => setPicker(row.kind)}>
                  Asociar existente
                </Button>
                <Button size="small" startIcon={busy
                  ? <CircularProgress size={14} /> : <CreateNewFolderIcon />}
                  disabled={busy} onClick={row.onCreate}>
                  Crear
                </Button>
              </>
            )}
          </Box>
        </Box>
      ))}

      {picker && (
        <DriveFolderPicker
          open
          rootPath={rows.find((r) => r.kind === picker).root}
          title={picker === 'offer'
            ? 'Asociar carpeta de oferta existente'
            : 'Asociar carpeta de proyecto existente'}
          description="Navega hasta la carpeta que corresponde a este proyecto y selecciónala. No se moverá ni se renombrará nada en Drive."
          onClose={() => setPicker(null)}
          onSelect={handleSelect(picker)}
        />
      )}
    </Paper>
  );
}
